import { ItemFavorite } from '@sbt-web/ui';
import React from 'react';
import { MnemosyneClient, HTTPStatusCode } from '@sbt-web/networking';
import Cookies from 'js-cookie';
import { getPublicUser } from '@sbt-web/auth';

const initialValue = {
    urnToStatusMap: {},
    getStatus: () => 'inactive',
    toggleFavorite: () => undefined,
};
const FavoritesContext = React.createContext(initialValue);

const FavoritesConsumer = (props) => (React.createElement(FavoritesContext.Consumer, null, ({ getStatus, toggleFavorite }) => (React.createElement(ItemFavorite, { onClick: (e) => {
        toggleFavorite(props.urn);
        if (props.onClick) {
            props.onClick(e);
        }
    }, status: getStatus(props.urn) }))));
FavoritesConsumer.displayName = 'FavoritesConsumer';

var FavoritesError;
(function (FavoritesError) {
    FavoritesError[FavoritesError["Sync"] = 0] = "Sync";
    FavoritesError[FavoritesError["Save"] = 1] = "Save";
    FavoritesError[FavoritesError["Unsave"] = 2] = "Unsave";
    FavoritesError[FavoritesError["Unknown"] = 3] = "Unknown";
})(FavoritesError || (FavoritesError = {}));
const localStorageKey = 'favorites';
const cachePurgeCookieName = 'favorites_update_cache';
const syncTimeout = 600000;
const defaultCache = () => ({
    favorites: [],
    last_sync: 0,
    user_id: '',
});
const getCache = () => {
    const cacheValue = window.localStorage.getItem(localStorageKey);
    if (cacheValue === null) {
        return defaultCache();
    }
    else {
        return JSON.parse(cacheValue);
    }
};
class FavoritesProvider extends React.Component {
    static displayName = 'FavoritesProvider';
    client;
    oldUserId;
    mounted = false;
    isSyncing = false;
    afterSyncQueue = [];
    cache = defaultCache();
    constructor(props) {
        super(props);
        this.client = new MnemosyneClient(props.APIBaseUrl, props.APIChannel);
        this.oldUserId = props.userId;
        this.state = {
            ctxValue: {
                ...initialValue,
                getStatus: this.getStatus,
                toggleFavorite: this.toggleFavorite,
            },
        };
    }
    async componentDidUpdate() {
        const userId = getPublicUser()?.id ?? this.props.userId;
        if (this.oldUserId !== userId) {
            this.oldUserId = userId;
            await this.init();
        }
    }
    async componentDidMount() {
        this.mounted = true;
        await this.init();
    }
    componentWillUnmount() {
        this.mounted = false;
    }
    componentDidCatch(error) {
        return this.props.onError(FavoritesError.Unknown, { error });
    }
    render() {
        const { children } = this.props;
        const { ctxValue } = this.state;
        return (React.createElement(FavoritesContext.Provider, { value: ctxValue }, children));
    }
    init = async () => {
        const userId = getPublicUser()?.id ?? this.props.userId;
        if (!userId) {
            try {
                window.localStorage.removeItem(localStorageKey);
            }
            catch {
                console.error('Could not clear the favourite cache for the unlogged user.');
            }
            return;
        }
        let cacheExists = false;
        try {
            cacheExists = window.localStorage.getItem(localStorageKey) != null;
        }
        catch {
            console.error('Could not access the cache.');
        }
        if (!cacheExists || Cookies.get(cachePurgeCookieName) === 'true') {
            Cookies.remove(cachePurgeCookieName);
            return await this.syncFavorites();
        }
        return await this.updateFromCache();
    };
    updateFromCache = async () => {
        const userId = getPublicUser()?.id ?? this.props.userId;
        const { ctxValue } = this.state;
        try {
            this.cache = getCache();
            if (userId != '') {
                const timeFromLastSync = Date.now() - this.cache.last_sync;
                if (this.cache.user_id !== userId || timeFromLastSync > syncTimeout) {
                    return await this.syncFavorites();
                }
                const urnToStatusMap = this.cache.favorites.reduce((acc, urn) => {
                    acc[urn] = 'active';
                    return acc;
                }, {});
                if (this.mounted) {
                    this.setState({
                        ctxValue: {
                            ...ctxValue,
                            urnToStatusMap,
                        },
                    });
                }
                window.setTimeout(() => this.updateFromCache(), syncTimeout - timeFromLastSync);
            }
        }
        catch {
            console.error('Could not set up favorites.');
        }
    };
    syncFavorites = async () => {
        if (this.isSyncing) {
            return;
        }
        this.isSyncing = true;
        const { onError } = this.props;
        const userId = getPublicUser()?.id ?? this.props.userId;
        const { ctxValue } = this.state;
        const response = await this.client.getFavorites(userId);
        const { status, favorites = [] } = response;
        if (status !== HTTPStatusCode.OK && status !== HTTPStatusCode.Accepted) {
            this.isSyncing = false;
            return onError(FavoritesError.Sync, { response });
        }
        this.cache.favorites = [];
        this.cache.last_sync = Date.now();
        this.cache.user_id = userId;
        const urnToStatusMap = favorites.reduce((acc, favorite) => {
            acc[favorite.urn] = 'active';
            this.cache.favorites.push(favorite.urn);
            return acc;
        }, {});
        try {
            window.localStorage.setItem(localStorageKey, JSON.stringify(this.cache));
        }
        catch {
            console.error('Could not update the favourite cache.');
        }
        if (this.mounted) {
            this.setState({ ctxValue: { ...ctxValue, urnToStatusMap } }, () => {
                this.isSyncing = false;
                this.flushQueue();
                window.setTimeout(() => this.updateFromCache(), syncTimeout);
            });
        }
    };
    toggleFavorite = (urn) => {
        const userId = getPublicUser()?.id ?? this.props.userId;
        if (userId == '') {
            return this.props.onError(FavoritesError.Save, { error: new Error(), urn }, () => {
                return this.toggleFavorite(urn);
            });
        }
        if (this.getStatus(urn) === 'loading') {
            return;
        }
        const { ctxValue } = this.state;
        const currentStatus = ctxValue.urnToStatusMap[urn];
        const isActive = currentStatus === 'active';
        this.setFavStatus(urn, 'loading');
        if (this.isSyncing) {
            this.afterSyncQueue.push(() => this.toggleFavorite(urn));
            return;
        }
        if (isActive) {
            this.requestStatusChange(this.client.unsaveFavorite.bind(this.client), urn, currentStatus, 'inactive', FavoritesError.Unsave, this.onUnsave);
        }
        else {
            if (this.props.onEngage) {
                this.props.onEngage(urn);
            }
            this.requestStatusChange(this.client.saveFavorite.bind(this.client), urn, currentStatus, 'active', FavoritesError.Save, this.onSave);
        }
    };
    requestStatusChange = (clientMethod, urn, prevStatus, nextStatus, error, onSuccess) => {
        const performStatusChange = () => {
            const { onError } = this.props;
            const userId = getPublicUser()?.id ?? this.props.userId;
            if (!userId) {
                // We are early exiting before doing the request because if we do not have the userId
                // we cannot create the request Url (e.g. when the user try to toggle a favorite when it is not logged in)
                return onError(error, { error: new Error(), urn }, performStatusChange);
            }
            clientMethod(urn, userId)
                .then((response) => {
                if (response.status === HTTPStatusCode.OK ||
                    response.status === HTTPStatusCode.NoContent) {
                    if (onSuccess) {
                        onSuccess(urn);
                    }
                    return this.setFavStatus(urn, nextStatus);
                }
                this.setFavStatus(urn, prevStatus);
                return onError(error, { response, urn }, performStatusChange);
            })
                .catch((error) => {
                this.setFavStatus(urn, prevStatus);
                return onError(error, { error, urn }, performStatusChange);
            });
        };
        performStatusChange();
    };
    getStatus = (urn) => {
        const { ctxValue } = this.state;
        return ctxValue.urnToStatusMap[urn] || 'inactive';
    };
    onUnsave = (urn) => {
        const { onUnsave } = this.props;
        this.deleteFavoriteFromCache(urn);
        if (onUnsave) {
            onUnsave(urn);
        }
    };
    onSave = (urn) => {
        const { onSave } = this.props;
        this.addFavoriteToCache(urn);
        if (onSave) {
            onSave(urn);
        }
    };
    setFavStatus = (urn, status) => {
        const { ctxValue } = this.state;
        if (this.mounted) {
            this.setState({
                ctxValue: {
                    ...ctxValue,
                    urnToStatusMap: { ...ctxValue.urnToStatusMap, [urn]: status },
                },
            });
        }
    };
    flushQueue = () => {
        while (this.afterSyncQueue.length) {
            const syncToDo = this.afterSyncQueue.pop();
            if (syncToDo) {
                syncToDo();
            }
        }
    };
    deleteFavoriteFromCache = (adUrn) => {
        if (this.cache.favorites.every((urn) => urn !== adUrn)) {
            return;
        }
        this.cache.favorites.splice(this.cache.favorites.indexOf(adUrn), 1);
        try {
            window.localStorage.setItem(localStorageKey, JSON.stringify(this.cache));
        }
        catch {
            console.error('Could not delete the item to the favourite cache.');
        }
    };
    addFavoriteToCache = (adUrn) => {
        if (this.cache.favorites.some((urn) => urn === adUrn)) {
            return;
        }
        this.cache.favorites.push(adUrn);
        try {
            window.localStorage.setItem(localStorageKey, JSON.stringify(this.cache));
        }
        catch {
            console.error('Could not add the new item to the favourite cache.');
        }
    };
}

export { FavoritesConsumer, FavoritesError, FavoritesProvider };
//# sourceMappingURL=index.js.map
